import { useState, useEffect } from "react";
import { useParams, Link } from "react-router";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Badge from "@/components/ui/badge/Badge";
import { Modal } from "@/components/ui/modal";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { systemAdminService } from "@/services/systemAdminService";
import { handleApiError } from "@/services/api";
import type { User, FarmListItem } from "@/types/models";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

const roleColors: Record<string, "primary" | "success" | "warning" | "info" | "dark"> = {
  admin: "primary",
  owner: "success",
  manager: "dark",
  supervisor: "warning",
  viewer: "info",
};

export default function FarmDetailPage() {
  const { id } = useParams<{ id: string }>();
  const farmId = Number(id);

  const [farm, setFarm] = useState<FarmListItem | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Reset PIN
  const [resetTarget, setResetTarget] = useState<User | null>(null);
  const [resetting, setResetting] = useState(false);

  // PIN result modal
  const [pinResult, setPinResult] = useState<{
    pin: string;
    userName: string;
    phone: string;
    email: string;
    farmName: string;
    context: string;
  } | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Edit user modal
  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editRole, setEditRole] = useState("owner");
  const [editActive, setEditActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    setError("");
    try {
      const [farms, farmUsers] = await Promise.all([
        systemAdminService.listFarms(),
        systemAdminService.listFarmUsers(farmId),
      ]);
      const farmList = Array.isArray(farms) ? farms : [];
      setFarm(farmList.find((f) => f.id === farmId) ?? null);
      setUsers(Array.isArray(farmUsers) ? farmUsers : []);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [farmId]);

  const handleResetPIN = async () => {
    if (!resetTarget) return;
    setResetting(true);
    try {
      const result = await systemAdminService.resetUserPIN(resetTarget.id);
      setResetTarget(null);
      setEmailSent(false);
      setPinResult({
        pin: result.pin,
        userName: resetTarget.name,
        phone: resetTarget.phone,
        email: resetTarget.email || "",
        farmName: farmName,
        context: `PIN reset for "${resetTarget.name}"`,
      });
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setResetting(false);
    }
  };

  const openEdit = (user: User) => {
    setEditTarget(user);
    setEditName(user.name);
    setEditPhone(user.phone);
    setEditEmail(user.email ?? "");
    setEditRole(user.role);
    setEditActive(user.active);
    setError("");
  };

  const closeEdit = () => {
    setEditTarget(null);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editTarget) return;
    setSaving(true);
    setError("");
    try {
      await systemAdminService.updateUser(editTarget.id, {
        name: editName,
        phone: editPhone,
        role: editRole,
        active: editActive,
        email: editEmail || undefined,
      });
      closeEdit();
      setLoading(true);
      await loadData();
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const farmName = farm?.name ?? `Farm #${farmId}`;

  return (
    <>
      <PageMeta title={`${farmName} Users | Poultry Book`} description="Manage farm users" />
      <PageBreadcrumb pageTitle={farmName} />

      <div className="mb-6">
        <Link
          to="/system"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Farms
        </Link>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white/90">
            {farmName}
          </h2>
          {farm && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Owner: {farm.owner_name || "—"} &middot; {farm.user_count} user{farm.user_count !== 1 ? "s" : ""} &middot; Created {new Date(farm.created_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <div className="px-5 py-4 sm:px-6">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90">Users</h3>
        </div>

        {error && !editTarget && (
          <div className="mx-5 mb-4 rounded-lg bg-error-50 px-4 py-3 dark:bg-error-500/10">
            <p className="text-sm text-error-600 dark:text-error-400">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
          </div>
        ) : users.length === 0 && !error ? (
          <div className="px-5 py-12 text-center text-gray-500 dark:text-gray-400">
            No users found for this farm.
          </div>
        ) : users.length === 0 ? null : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-t border-gray-200 dark:border-gray-800">
                  <TableCell isHeader className="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Name</TableCell>
                  <TableCell isHeader className="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Phone</TableCell>
                  <TableCell isHeader className="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Email</TableCell>
                  <TableCell isHeader className="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Role</TableCell>
                  <TableCell isHeader className="whitespace-nowrap px-5 py-3 text-left text-sm font-medium text-gray-500 dark:text-gray-400">Status</TableCell>
                  <TableCell isHeader className="whitespace-nowrap px-5 py-3 text-right text-sm font-medium text-gray-500 dark:text-gray-400">Actions</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} className="border-t border-gray-100 dark:border-gray-800">
                    <TableCell className="whitespace-nowrap px-5 py-3 text-sm font-medium text-gray-800 dark:text-white/90">
                      {user.name}
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {user.phone}
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-5 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {user.email || "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-5 py-3">
                      <Badge size="sm" color={roleColors[user.role] || "info"}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-5 py-3">
                      <Badge size="sm" color={user.active ? "success" : "error"}>
                        {user.active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap px-5 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => openEdit(user)}
                          className="text-sm text-brand-500 hover:text-brand-600 dark:text-brand-400"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setResetTarget(user)}
                          className="text-sm text-warning-500 hover:text-warning-600"
                        >
                          Reset PIN
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      <Modal isOpen={!!editTarget} onClose={closeEdit} className="max-w-md p-5 sm:p-8 mx-4">
        {editTarget && (
          <>
            <h4 className="mb-5 text-lg font-semibold text-gray-800 dark:text-white/90">
              Edit User
            </h4>

            {error && editTarget && (
              <div className="mb-4 rounded-lg bg-error-50 p-3 text-sm text-error-600 dark:bg-error-500/15 dark:text-error-500">
                {error}
              </div>
            )}

            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <Label htmlFor="editName">Name *</Label>
                <Input
                  id="editName"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                  maxLength={100}
                />
              </div>
              <div>
                <Label htmlFor="editPhone">Phone *</Label>
                <Input
                  id="editPhone"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  required
                  minLength={10}
                  maxLength={15}
                />
              </div>
              <div>
                <Label htmlFor="editEmail">Email</Label>
                <Input
                  id="editEmail"
                  type="email"
                  value={editEmail}
                  onChange={(e) => setEditEmail(e.target.value)}
                  placeholder="e.g. user@example.com"
                  maxLength={255}
                />
              </div>
              <div>
                <Label htmlFor="editRole">Role *</Label>
                <select
                  id="editRole"
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  className={selectClass}
                >
                  <option value="owner">Owner</option>
                  <option value="manager">Manager</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="viewer">Viewer (read-only)</option>
                </select>
              </div>
              <div className="flex items-center gap-3">
                <input
                  id="editActive"
                  type="checkbox"
                  checked={editActive}
                  onChange={(e) => setEditActive(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                />
                <Label htmlFor="editActive">Active</Label>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" size="sm" onClick={closeEdit}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={saving || !editName || !editPhone}>
                  {saving ? "Saving..." : "Update"}
                </Button>
              </div>
            </form>
          </>
        )}
      </Modal>

      {/* Reset PIN Confirmation */}
      <Modal isOpen={!!resetTarget} onClose={() => setResetTarget(null)} className="max-w-sm p-5 sm:p-8 mx-4">
        {resetTarget && (
          <>
            <h4 className="mb-2 text-lg font-semibold text-gray-800 dark:text-white/90">
              Reset PIN
            </h4>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to reset the PIN for{" "}
              <strong>{resetTarget.name}</strong> ({resetTarget.phone})? A new random PIN will be generated.
            </p>
            <div className="flex justify-end gap-3">
              <Button size="sm" variant="outline" onClick={() => setResetTarget(null)}>
                Cancel
              </Button>
              <Button size="sm" disabled={resetting} onClick={handleResetPIN}>
                {resetting ? "Resetting..." : "Reset PIN"}
              </Button>
            </div>
          </>
        )}
      </Modal>

      {/* PIN Result Modal */}
      <Modal isOpen={!!pinResult} onClose={() => setPinResult(null)} showCloseButton={false} className="max-w-sm p-5 sm:p-8 mx-4">
        {pinResult && (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-success-50 dark:bg-success-500/10">
              <svg className="h-6 w-6 text-success-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="mb-2 text-sm text-gray-600 dark:text-gray-400">{pinResult.context}</p>
            <p className="mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
              PIN for {pinResult.userName}:
            </p>
            <div className="my-4 flex items-center justify-center gap-3">
              <span className="rounded-lg bg-gray-100 px-6 py-3 font-mono text-2xl font-bold tracking-[0.3em] text-gray-900 dark:bg-gray-800 dark:text-white">
                {pinResult.pin}
              </span>
              <button
                onClick={() => copyToClipboard(pinResult.pin)}
                className="rounded-lg border border-gray-300 p-2 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
                title="Copy PIN"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
            <p className="mb-4 text-xs text-warning-500 dark:text-warning-400">
              This PIN will not be shown again. Please note it down.
            </p>
            <div className="flex flex-col items-center gap-3">
              {pinResult.email && !emailSent && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  disabled={sendingEmail}
                  onClick={async () => {
                    setSendingEmail(true);
                    try {
                      await systemAdminService.sendPINEmail({
                        email: pinResult.email,
                        user_name: pinResult.userName,
                        phone: pinResult.phone,
                        farm_name: pinResult.farmName,
                        pin: pinResult.pin,
                      });
                      setEmailSent(true);
                    } catch (err) {
                      setError(handleApiError(err));
                    } finally {
                      setSendingEmail(false);
                    }
                  }}
                >
                  {sendingEmail ? "Sending..." : "Send PIN via Email"}
                </Button>
              )}
              {emailSent && (
                <span className="text-sm text-success-500">Sent to {pinResult.email}</span>
              )}
              <Button size="sm" onClick={() => setPinResult(null)}>Done</Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
